import React, { useState } from "react";
import { Card, Form, Row, Col, Button } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Block from "../../components/Block/Block";
import { Select, Icon } from "../../components";
import { createEmployeeApi } from "../../services/UserService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { deptOptions } from "../../utilities";
import { Spinner } from 'react-bootstrap';


const GENDER_OPTIONS = ["Male", "Female", "Other"];

const EmployeeAddPage = () => {
  const emptyFormData = {
    first_name: "",
    last_name: "",
    email: "",
    mobile_number: "",
    password: "",
    employee_id: "",
    department: "",
    gender: "",
  };

  const [formData, setFormData] = useState(emptyFormData);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const navigate = useNavigate();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePasswordToggle = () => {
    setPasswordVisible(!passwordVisible);
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileNumberRegex = /^[0-9]{10}$/;
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%?&])[A-Za-z\d@$!%?&]{8,}$/;

    if (!formData.first_name) newErrors.first_name = "First name is required";
    if (!formData.last_name) newErrors.last_name = "Last name is required";

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.mobile_number) {
      newErrors.mobile_number = "Mobile Number is required";
    } else if (!mobileNumberRegex.test(formData.mobile_number)) {
      newErrors.mobile_number = "Mobile Number must be 10 digits.";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (!passwordRegex.test(formData.password)) {
      newErrors.password =
        "Password must contain at least 8 characters, one uppercase, one lowercase, one number, and one special character.";
    }

    if (!formData.employee_id)
      newErrors.employee_id = "Employee ID is required";
    if (!formData.department) newErrors.department = "Department is required";
    if (!formData.gender) newErrors.gender = "Gender is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setIsSubmitLoading(true);
      const response = await createEmployeeApi(formData);
      setErrors({});
      toast.success(response?.data?.message);
      setTimeout(() => navigate("/employees"), 1500);
    } catch (error) {
      const backendErrors = error.response?.data;

      if (backendErrors) {
        // Handle field-specific validation errors
        if (
          typeof backendErrors === "object" &&
          !Array.isArray(backendErrors)
        ) {
          const fieldErrors = {};

          // Map backend errors to form fields
          for (const [field, message] of Object.entries(backendErrors)) {
            if (
              field === "email" ||
              field === "mobile_number" ||
              field === "employee_id"
            ) {
              fieldErrors[field] = Array.isArray(message)
                ? message[0]
                : message;
            } else if (field === "non_field_errors") {
              // Handle non-field errors
              toast.error(Array.isArray(message) ? message[0] : message);
            }
          }

          if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
          } else {
            // Show generic error if no specific field errors
            toast.error("Please correct the errors in the form.");
          }
        } else {
          // Handle other error formats
          toast.error(
            backendErrors.message || "An error occurred while creating employee"
          );
        }
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData(emptyFormData);
    setErrors({});
    navigate("/employees");
  };

  return (
    <div>
      <Block.Head>
        <Block.HeadBetween>
          <Block.HeadContent>
            <Block.Title tag="h2">Add New Employee</Block.Title>
            <nav>
              <ol className="breadcrumb breadcrumb-arrow mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/employees">Employees</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Add Employee
                </li>
              </ol>
            </nav>
          </Block.HeadContent>
        </Block.HeadBetween>
      </Block.Head>

      <Block>
        <Form onSubmit={handleSubmit}>
          <Row className="g-gs">
            <Col xxl="12">
              <div className="gap gy-4">
                <div className="gap-col">
                  <Card className="card-gutter-md">
                    <Card.Body>
                      <h4 className="bio-block-title mb-4">
                        Enter Employee Details
                      </h4>
                      <Row className="g-gs">
                        {/* Name Fields */}
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              First Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="first_name"
                              placeholder="Enter First Name"
                              value={formData.first_name}
                              onChange={handleChange}
                              isInvalid={!!errors.first_name}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.first_name}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              Last Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="last_name"
                              placeholder="Enter Last Name"
                              value={formData.last_name}
                              onChange={handleChange}
                              isInvalid={!!errors.last_name}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.last_name}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        {/* Email */}
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              Email <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="email"
                              name="email"
                              placeholder="Enter Email"
                              value={formData.email}
                              onChange={handleChange}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.email}
                            </Form.Control.Feedback>
                            {errors.email && (
                              <div className="text-danger small mt-1">
                                <Icon name="info-circle" className="me-1" />
                                {errors.email}
                              </div>
                            )}
                          </Form.Group>
                          
                        </Col>

                        {/* Phone Number */}
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              Phone Number{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="mobile_number"
                              placeholder="Enter Phone Number"
                              value={formData.mobile_number}
                              onChange={handleChange}
                              isInvalid={!!errors.mobile_number}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.mobile_number}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        {/* Employee ID */}
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              Employee ID <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="employee_id"
                              placeholder="Enter Employee ID"
                              value={formData.employee_id}
                              onChange={handleChange}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.employee_id}
                            </Form.Control.Feedback>
                            {errors.employee_id && (
                              <div className="text-danger small mt-1">
                                <Icon name="info-circle" className="me-1" />
                                {errors.employee_id}
                              </div>
                            )}
                          </Form.Group>
                        </Col>

                        {/* Password */}
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              Password <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="position-relative">
                              <Form.Control
                                type={passwordVisible ? "text" : "password"}
                                name="password"
                                placeholder="Enter Password"
                                value={formData.password}
                                onChange={handleChange}
                                isInvalid={!!errors.password}
                                autoComplete="new-password"
                                className="pe-5"
                              />
                              <span
                                onClick={handlePasswordToggle}
                                className="position-absolute end-0 top-50 translate-middle-y me-2"
                                style={{ cursor: "pointer" }}
                              >
                                <Icon
                                  name={
                                    passwordVisible
                                      ? "eye-alt-fill"
                                      : "eye-off-fill"
                                  }
                                />
                              </span>
                              <Form.Control.Feedback type="invalid">
                                {errors.password}
                              </Form.Control.Feedback>
                            </div>
                          </Form.Group>
                        </Col>

                        {/* Department */}
                        <Col lg="6">
                          <Form.Group>
                            <Form.Label>
                              Department <span className="text-danger">*</span>
                            </Form.Label>
                            <Select
                              id="department"
                              name="department"
                              value={formData.department}
                              onChange={handleSelectChange}
                              isInvalid={!!errors.department}
                            >
                              <option value="">Choose Department</option>
                              {deptOptions?.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Select>
                            {errors.department && (
                              <div className="text-danger small mt-1">
                                {errors.department}
                              </div>
                            )}
                          </Form.Group>
                        </Col>

                        {/* Gender */}
                        <Col lg="6">
                          <Form.Group>
                            <Form.Label>
                              Gender <span className="text-danger">*</span>
                            </Form.Label>
                            <br />
                            {GENDER_OPTIONS.map((option) => (
                              <Form.Check
                                key={option}
                                inline
                                type="radio"
                                label={option}
                                name="gender"
                                value={option}
                                checked={formData.gender === option}
                                onChange={handleChange}
                                className="me-4"
                                isInvalid={!!errors.gender}
                              />
                            ))}
                            {errors.gender && (
                              <div className="text-danger small mt-1">
                                {errors.gender}
                              </div>
                            )}
                          </Form.Group>
                        </Col>

                        {/* Buttons */}
                        <div className="d-flex justify-content-center mt-4">
                          <Button
                            variant="primary"
                            type="submit"
                            disabled={isSubmitLoading}
                            className="me-3"
                          >
                            {isSubmitLoading ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                />
                                <span className="ms-2">Submitting...</span>
                              </>
                            ) : (
                              "Submit"
                            )}
                          </Button>
                          <Button variant="secondary" onClick={handleCancel}>
                            Cancel
                          </Button>
                        </div>
                      </Row>
                    </Card.Body>
                  </Card>
                </div>
              </div>
            </Col>
          </Row>
        </Form>
      </Block>
    </div>
  );
};

export default EmployeeAddPage;
